// src/components/notifications/AwarenessNotificationCard.js

import React, { useEffect, useRef, useState } from 'react';

import piNetworkLogo from '../../assets/pinetworklogo-icon.jpg';
import verifiedIcon from '../../assets/newsgoldtick-icon.png';
import discussIcon from '../../assets/discuss-icon.png';
import dotsIcon from '../../assets/dots-icon.png';

import './AwarenessNotificationCard.css';

function formatPublishedDate(value) {
  if (!value) return '';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return '';

  try {
    return new Intl.DateTimeFormat(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  } catch {
    return date.toLocaleString();
  }
}

function AwarenessNotificationCard({
  notification,
  onOpen,
  onHide,
  onPreferenceChange,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const menuRef = useRef(null);

  const article = notification?.article || {};
  const campaignId = notification?.campaignId || notification?._id || '';
  const isRead = Boolean(notification?.isRead);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [menuOpen]);

  const handleOpen = async () => {
    if (actionLoading || !campaignId) return;

    try {
      setActionLoading(true);
      await onOpen?.(notification);
    } finally {
      setActionLoading(false);
    }
  };

  const handleHide = async () => {
    if (actionLoading || !campaignId) return;

    try {
      setActionLoading(true);
      setMenuOpen(false);
      await onHide?.(notification);
    } finally {
      setActionLoading(false);
    }
  };

  const handlePreferenceChange = async (value) => {
    if (actionLoading) return;

    try {
      setActionLoading(true);
      setMenuOpen(false);
      await onPreferenceChange?.(value);
    } finally {
      setActionLoading(false);
    }
  };

  const handleMenuClick = (event) => {
    event.stopPropagation();
    setMenuOpen((current) => !current);
  };

  return (
    <article className={`awareness-notification-card ${isRead ? 'read' : 'unread'}`}>
      <div className="awareness-notification-top">
        <button
          type="button"
          className="awareness-notification-main"
          onClick={handleOpen}
          disabled={actionLoading}
          aria-label={`Open ${article.title || 'Pi Network update'}`}
        >
          <div className="awareness-notification-source-row">
            <img
              src={piNetworkLogo}
              alt="Pi Network"
              className="awareness-notification-logo"
            />

            <div className="awareness-notification-source-copy">
              <div className="awareness-notification-source-name">
                <span>{article.sourceName || 'Pi Network'}</span>

                {article.isVerifiedSource && (
                  <img
                    src={verifiedIcon}
                    alt="Verified source"
                    className="awareness-notification-verified"
                  />
                )}
              </div>

              <span className="awareness-notification-label">
                {notification?.headline || 'Official Pi update'}
              </span>
            </div>
          </div>
        </button>

        <div className="awareness-notification-menu-wrap" ref={menuRef}>
          <button
            type="button"
            className="awareness-notification-menu-button"
            onClick={handleMenuClick}
            aria-label="Open Pi News notification options"
            aria-expanded={menuOpen}
          >
            <img
              src={dotsIcon}
              alt=""
              className="awareness-notification-menu-icon"
            />
          </button>

          {menuOpen && (
            <div className="awareness-notification-menu">
              <button type="button" onClick={handleHide}>
                Hide this update
              </button>

              <button
                type="button"
                onClick={() => handlePreferenceChange('reduced')}
              >
                See fewer Pi News updates
              </button>

              <button
                type="button"
                onClick={() => handlePreferenceChange('off')}
              >
                Turn off Pi News updates
              </button>
            </div>
          )}
        </div>
      </div>

      <button
        type="button"
        className="awareness-notification-content"
        onClick={handleOpen}
        disabled={actionLoading}
      >
        {article.imageUrl && (
          <div className="awareness-notification-banner-wrap">
            <img
              src={article.imageUrl}
              alt=""
              className="awareness-notification-banner"
            />
          </div>
        )}

        <div className="awareness-notification-body">
          <h3 className="awareness-notification-title">
            {article.title || 'New official Pi Network update'}
          </h3>

          {article.excerpt && (
            <p className="awareness-notification-excerpt">
              {article.excerpt}
            </p>
          )}

          <div className="awareness-notification-footer">
            <span className="awareness-notification-time">
              {formatPublishedDate(article.publishedAt || notification?.createdAt)}
            </span>

            <span className="awareness-notification-action">
              <img src={discussIcon} alt="" />
              Join discussion
            </span>
          </div>
        </div>
      </button>
    </article>
  );
}

export default AwarenessNotificationCard;
